import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFolder, 
  faFolderOpen, 
  faVideo, 
  faArrowLeft, 
  faSync,
  faEye,
  faHeart,
  faComment,
  faCheck,
  faClock,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import './TikTokViewer.css';
import { API_ENDPOINTS } from '../../config/api';
import { toast } from 'react-hot-toast';

const TikTokViewer = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('clients'); // 'clients', 'sessions', 'files'
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchClientsWithTikToks();
  }, []);

  const fetchClientsWithTikToks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.TIKTOK.CLIENTS_LIST);
      console.log('Clients response:', response.data);
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientSessions = async (clientId) => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.TIKTOK.SESSIONS(clientId));
      console.log('Sessions response:', response.data);
      setSelectedClient(response.data);
      setView('sessions');
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleClientClick = (client) => {
    fetchClientSessions(client.client_id);
  };

  const handleSessionClick = (session) => {
    setSelectedSession(session);
    setView('files');
  };

  const handleBackToClients = () => {
    setSelectedClient(null);
    setSelectedSession(null);
    setView('clients');
  };

  const handleBackToSessions = () => {
    setSelectedSession(null);
    setView('sessions');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const renderClientsView = () => {
    // Filter clients based on search query
    const filteredClients = clients.filter(client => 
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.client_id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="tiktok-viewer-content">
        <div className="tiktok-viewer-header">
          <h2>Clients with TikTok Downloads</h2>
          <div className="tiktok-viewer-header-actions">
            <input
              type="text"
              className="tiktok-viewer-search-input"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button 
              className="tiktok-viewer-refresh-button"
              onClick={fetchClientsWithTikToks}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSync} spin={loading} /> Refresh
            </button>
          </div>
        </div>
        
        {searchQuery && (
          <div className="tiktok-viewer-search-results-info">
            Found {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
            {filteredClients.length !== clients.length && ` out of ${clients.length} total`}
          </div>
        )}
        
        {loading ? (
          <div className="tiktok-viewer-loading-state">Loading clients...</div>
        ) : filteredClients.length === 0 ? (
          <div className="tiktok-viewer-empty-state">
            <FontAwesomeIcon icon={faFolder} size="3x" />
            <p>{searchQuery ? 'No clients found matching your search' : 'No clients with TikTok downloads found'}</p>
          </div>
        ) : (
          <div className="tiktok-viewer-clients-grid">
            {filteredClients.map((client) => (
            <div 
              key={client.client_id}
              className="tiktok-viewer-client-card"
              onClick={() => handleClientClick(client)}
            >
              <div className="tiktok-viewer-client-card-icon">
                <FontAwesomeIcon icon={faFolder} size="2x" />
              </div>
              <div className="tiktok-viewer-client-card-content">
                <h3>{client.name}</h3>
                <div className="tiktok-viewer-client-stats">
                  <span className="tiktok-viewer-stat">
                    <FontAwesomeIcon icon={faVideo} />
                    {client.total_sessions} session{client.total_sessions !== 1 ? 's' : ''}
                  </span>
                  <span className="tiktok-viewer-stat">
                    {client.total_videos} video{client.total_videos !== 1 ? 's' : ''}
                  </span>
                </div>
                {client.last_upload && (
                  <div className="tiktok-viewer-client-last-upload">
                    Last upload: {formatDate(client.last_upload)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    );
  };

  const renderSessionsView = () => (
    <div className="tiktok-viewer-content">
      <div className="tiktok-viewer-header">
        <button className="tiktok-viewer-back-button" onClick={handleBackToClients}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Clients
        </button>
        <h2>{selectedClient?.name}</h2>
      </div>

      {loading ? (
        <div className="loading-state">Loading sessions...</div>
      ) : !selectedClient?.tt_sessions || selectedClient.tt_sessions.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faFolderOpen} size="3x" />
          <p>No TikTok sessions found for this client</p>
        </div>
      ) : (
        <div className="sessions-list">
          {selectedClient.tt_sessions.map((session, index) => (
            <div 
              key={session.session_id || index}
              className="tiktok-viewer-session-card"
              onClick={() => handleSessionClick(session)}
            >
              <div className="tiktok-viewer-session-icon">
                <FontAwesomeIcon icon={faFolderOpen} size="2x" />
              </div>
              <div className="tiktok-viewer-session-info">
                <h3>{session.session_id}</h3>
                <div className="tiktok-viewer-session-meta">
                  <span className="tiktok-viewer-meta-item">
                    <FontAwesomeIcon icon={faVideo} />
                    {session.total_videos || session.files?.length || 0} videos
                  </span>
                  <span className="tiktok-viewer-meta-item">
                    <FontAwesomeIcon icon={faClock} />
                    {formatDate(session.upload_date)}
                  </span>
                  {session.files && (
                    <span className="tiktok-viewer-meta-item">
                      <FontAwesomeIcon icon={faCheck} />
                      {session.files.filter(f => f.queued).length} queued
                    </span>
                  )}
                </div>
                <div className="tiktok-viewer-session-path">{session.folder_path}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderFilesView = () => (
    <div className="tiktok-viewer-content">
      <div className="tiktok-viewer-header">
        <button className="tiktok-viewer-back-button" onClick={handleBackToSessions}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Sessions
        </button>
        <div className="tiktok-viewer-session-header-info">
          <h2>{selectedSession?.session_id}</h2>
          <span className="tiktok-viewer-video-count">{selectedSession?.files?.length || 0} videos</span>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading files...</div>
      ) : !selectedSession?.files || selectedSession.files.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faVideo} size="3x" />
          <p>No videos found in this session</p>
        </div>
      ) : (
        <div className="files-list">
          {selectedSession.files.map((file, index) => (
            <div key={index} className="tiktok-viewer-file-row">
              {/* Left side - Video player */}
              <div className="tiktok-viewer-file-video-section">
                <video 
                  src={file.cdn_url} 
                  controls
                  preload="metadata"
                  className="tiktok-viewer-video-player-compact"
                />
              </div>

              {/* Right side - Info */}
              <div className="tiktok-viewer-file-details-section">
                <div className="tiktok-viewer-file-header-row">
                  <h4 className="tiktok-viewer-file-name">{file.file_name}</h4>
                  
                  {/* Status badges */}
                  <div className="tiktok-viewer-file-badges">
                    {file.queued && (
                      <span className="tiktok-viewer-badge badge-queued">
                        <FontAwesomeIcon icon={faCheck} /> Queued
                      </span>
                    )}
                    {file.posted && (
                      <span className="tiktok-viewer-badge badge-posted">Posted</span>
                    )}
                    {file.selection_tier && (
                      <span className={`badge badge-tier badge-${file.selection_tier}`}>
                        {file.selection_tier}
                      </span>
                    )}
                  </div>
                </div>

                {/* Caption */}
                {file.caption && (
                  <div className="tiktok-viewer-file-caption-compact">
                    {file.caption}
                  </div>
                )}

                {/* Stats and metadata in columns */}
                <div className="tiktok-viewer-file-info-grid">
                  {/* Stats */}
                  {file.stats && (file.stats.views > 0 || file.stats.view_count > 0 || file.stats.likes > 0 || file.stats.like_count > 0) && (
                    <div className="tiktok-viewer-info-column">
                      <div className="tiktok-viewer-info-label">Engagement</div>
                      <div className="tiktok-viewer-file-stats-compact">
                        {(file.stats.views > 0 || file.stats.view_count > 0) && (
                          <div className="tiktok-viewer-stat-item">
                            <FontAwesomeIcon icon={faEye} />
                            <span>{formatNumber(file.stats.views || file.stats.view_count || 0)}</span>
                          </div>
                        )}
                        {(file.stats.likes > 0 || file.stats.like_count > 0) && (
                          <div className="tiktok-viewer-stat-item">
                            <FontAwesomeIcon icon={faHeart} />
                            <span>{formatNumber(file.stats.likes || file.stats.like_count || 0)}</span>
                          </div>
                        )}
                        {(file.stats.comments > 0 || file.stats.comment_count > 0) && (
                          <div className="tiktok-viewer-stat-item">
                            <FontAwesomeIcon icon={faComment} />
                            <span>{formatNumber(file.stats.comments || file.stats.comment_count || 0)}</span>
                          </div>
                        )}
                      </div>
                      {file.performance_score && file.performance_score > 0 && (
                        <div className="tiktok-viewer-file-performance-compact">
                          <FontAwesomeIcon icon={faChartLine} />
                          <span>Score: {formatNumber(file.performance_score)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="tiktok-viewer-info-column">
                    <div className="tiktok-viewer-info-label">File Info</div>
                    <div className="tiktok-viewer-file-metadata-compact">
                      <div>Size: {formatFileSize(file.size)}</div>
                      {file.last_modified && (
                        <div>Modified: {formatDate(file.last_modified)}</div>
                      )}
                      {file.posted_at && (
                        <div>Posted: {formatDate(file.posted_at)}</div>
                      )}
                    </div>
                  </div>

                  {/* URL */}
                  <div className="tiktok-viewer-info-column">
                    <div className="tiktok-viewer-info-label">CDN</div>
                    <a 
                      href={file.cdn_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="tiktok-viewer-file-url-compact"
                    >
                      View Full URL →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="tiktok-viewer-container">
      {view === 'clients' && renderClientsView()}
      {view === 'sessions' && renderSessionsView()}
      {view === 'files' && renderFilesView()}
    </div>
  );
};

export default TikTokViewer;

